/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react'
import { useRequest } from '../../hooks/UseRequest'
import Loading from '../../components/Loading'
import { useDispatch, useSelector } from 'react-redux'
import { selectModules } from '../../redux/moduleSlice'
import DashletTooltip from '../../components/elements/DashletTooltip'
import NoRecord from '../../components/elements/NoRecord'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faCheck, faClose, faX } from '@fortawesome/free-solid-svg-icons'
import { setNotification } from '../../redux/notificationSlice'
import { setModal } from '../../redux/modalSlice'
import { triggerRerender } from '../../redux/rerenderSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormatISODate } from '../../helpers/formatting'

export default function VerificationDashlet({ dropdownOptions, customerId, isLocked, setVerificationId, setStartVerification, setStep }) {
  const dispatch = useDispatch();
  const requests = useRequest()
  const requesting = useRef(false)
  const [row, setRow] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [module, setModule] = useState()
  const modules = useSelector(selectModules)
  const [saving, setSaving] = useState(false)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search);
  const navi = useNavigate()
  const processingStatusCompletedId = dropdownOptions.find((o) => o?.code_key === 'processing_status_completed')?.id

  useEffect(() => {
    if (!requesting.current && row.length === 0) {
      getStepDetails()
    }
  }, [customerId])

  const getStepDetails = async () => {
    setLoaded(false)
    requesting.current = true
    const leadGuid = queryParams.get('lead');
    if (leadGuid) {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerId}/lead/${leadGuid}/step_details`)
    } else {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerId}/step_details`)
    }
    try {
      const response = await requests.get()
      setRow(response.data.data)
      const m = modules.data.find((e) => e.code_key === 'verification_response')
      setModule(m)
      setLoaded(true)
    } catch (error) {
      setRow([])
      setLoaded(true)
    }
  }

  const renderHeader = (column_name, customize_label = false) => {
    if (customize_label) {
      return column_name;
    }
    const data = module.module_fields.find(
      (header) => header.column_name === column_name
    )
    if (data && !data.helper_text) {
      return data?.label_name
    }
    return <DashletTooltip columnName={data?.label_name} tooltipText={data?.helper_text} />
  }

  /**
   * Reset Manual Verification back to Step 1, non started and clear out all verification records so that the customer
   *  can complete SSV again.
   * @returns {Promise<void>}
   */
  const resetSSV = async () => {
    try {
      setSaving(true)
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/reset_ssv/${row?.latest_lead?.id}`)
      const response = await requests.post()

      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
      } else {
        // If the operation was not successful, show an error notification
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }

      setVerificationId(0)
      setStartVerification(false)
      setStep('questions')
      setSaving(false)

      getStepDetails()

      navi(`/manual_verification/${customerId}`, { state: { reloadPage: true } });
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      setSaving(false);
    }
  }

  const handleResetSSVClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'are_you_sure',
        options: {
          title: "Reset SSV",
          description: "Are you sure to reset SSV",
          actionLabel: "Yes",
          callMethod: () => { resetSSV() }
        }
      })
    )
  }

  return (
    <div className="message is-white lox-dashlet-detail">
      <div className="message-header has-background-primary has-text-white lox-dashlet-detail__title py-0">
        <p className="is-size-4">Verification Overview</p>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            type="submit"
            className="button is-link is-small has-text-weight-medium"
            disabled={!(row?.latest_lead && row?.can_reset_ssv && !saving && !isLocked)}
            onClick={handleResetSSVClick}
            style={{ height: '1.75em', marginRight: "1em" }}
          >
            {saving ? 'Resetting' : 'Reset SSV'}
          </button>
          <p className="is-size-4">
            <span
              className="is-clickable"
              onClick={() => getStepDetails()}
            >
              <FontAwesomeIcon icon={faRefresh} className={`${!loaded ? 'fa-pulse' : ''}`} />
            </span>
          </p>
        </div>
      </div>
      <div className="message-body py-0 px-0" style={{ lineHeight: 1.40 }}>
        {loaded && row && (
          <>
            {row?.latest_lead && (
              <div className="columns m-0">
                <div
                  className={`column py-1 has-text-centered px-0 ${row?.latest_lead?.is_redirected_to_our_site ? 'has-background-success' : 'has-background-danger has-text-info-light'}`}
                >
                  <span className="lox-dashlet-detail__item__label is-size-8 ">
                    <FontAwesomeIcon icon={(row?.latest_lead?.is_redirected_to_our_site ? faCheck : faClose)} /> Redirected to site
                  </span>
                </div>
                <div
                  className={`column ml-1 py-1 px-0 ${row?.latest_lead?.is_ssv_extra_ribbitbanklogin ? 'has-background-success' : 'has-background-danger has-text-info-light'}`}
                >
                  <div className="lox-dashlet-detail__item py-0 px-1 has-text-centered">
                    <span className="lox-dashlet-detail__item__label is-size-8">
                      <FontAwesomeIcon icon={(row?.latest_lead?.is_ssv_extra_ribbitbanklogin ? faCheck : faClose)} /> Ribbit Login Required
                    </span>
                  </div>
                </div>
              </div>
            )}

            {row?.document_verification?.is_drivers_license_verification_required ? (() => {
              const isCompleted = row?.document_verification?.document_verification_status_dropdown_option_id === processingStatusCompletedId;

              return (
                <div className="columns m-0">
                  <div className={`column mt-1 py-1 has-text-centered px-0 ${isCompleted ? 'has-background-success' : 'has-background-danger has-text-info-light'}`}>
                    <span className="lox-dashlet-detail__item__label is-size-8 ">
                      <FontAwesomeIcon icon={isCompleted ? faCheck : faClose} />
                      {' '}
                      {isCompleted ? 'DocumentID Completed' : 'DocumentID Required'}
                    </span>
                  </div>
                </div>
              );
            })() : null}

            {row?.latest_lead?.is_ssv_extra_ribbitbanklogin ? (
              <>
                <div className="lox-dashlet-detail__item py-0 px-1">
                  <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                    Ribbit Login Status
                  </p>
                  <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                    {dropdownOptions[row.ribbit_login_data.processing_status_dropdown_option_id]?.name} - {row.ribbit_login_data.updated_at ? FormatISODate(row.ribbit_login_data.updated_at) : 'Not Started'}
                  </p>
                </div>
                <div className="divider m-0 mx-1"></div>
              </>
            ) : ''}

            {row?.latest_lead && (
              <>
                <div className="lox-dashlet-detail__item py-0 px-1">
                  <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                    <DashletTooltip
                      columnName={"Consented to Payment Dates"}
                      tooltipText={"Did the customer agree to the presented payment dates in SSV? If the customer selects no they will get sent to phone verification. This will not block verifications from continuing with an Agent."}
                    />
                  </p>
                  <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                    {row?.latest_lead?.payment_date_consent ? 'Yes' : 'No'}
                  </p>
                </div>

                <div className="divider m-0 mx-1"></div>

                <div className="lox-dashlet-detail__item py-0 px-1">
                  <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                    <DashletTooltip
                      columnName={"Payment Dates Are Misaligned"}
                      tooltipText={"If the customer is semi-monthly are the payment dates within 11 days to each other? If this is the case then the schedule would be invalid so the customer will get sent to phone verification. This will not block verifications from continuing with an Agent."}
                    />
                  </p>
                  <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                    {row?.latest_lead?.payment_dates_misaligned ? 'Yes' : 'No'}
                  </p>
                </div>

                <div className="divider m-0 mx-1"></div>
              </>
            )}

            {row?.verification_steps?.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                {index > 0 && <div className="divider m-0 mx-1"></div>}
                <div className="lox-dashlet-detail__item py-0 px-1">
                  <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                    {renderHeader('SSV ' + dropdownOptions.find((x) => x?.id === item?.ssv_step_dropdown_option_id)?.name, true)}
                  </p>
                  <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                    SSV Status: {item.processing_status} - {FormatISODate(item?.updated_at)}
                  </p>
                </div>
              </React.Fragment>
            ))}
          </>
        )}
        {!loaded && <Loading />}
        {loaded && row.length === 0 && <NoRecord />}
      </div>
    </div>
  )
}
